import { useEffect, useMemo, useState } from 'react';

import { getKbDrawer } from '../api';
import { usePersistentState } from '../state/storage';
import type { KbDrawerPayload } from '../types';

/**
 * Slide-in KB drawer.
 *
 * Renders the raw ServiceNow-authored HTML body, scoped via `.kb-article`
 * CSS overrides. Slides in from the right; the Maximize button flips it
 * to a full-viewport overlay (preference persisted). Escape / outside
 * click / Close button all dismiss.
 *
 * The structured "Pretty view" was retired — reviewers preferred the raw
 * view (it matches what they see in ServiceNow), and the parser-driven
 * structured payload is already mined to populate the form-detail
 * sections (Form Identity, Approvals, Country availability, etc.).
 */
export function KBDrawer({
  kbNumber,
  onClose,
}: {
  kbNumber: string;
  onClose: () => void;
}) {
  const [maximized, setMaximized] = usePersistentState('intake.ui.kbDrawer.max.v1', false);
  const [data, setData] = useState<KbDrawerPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getKbDrawer(kbNumber)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kbNumber]);

  // Escape closes the drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll while drawer is open (in maximized mode it owns the viewport;
  // in side mode the page underneath shouldn't compete).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ServiceNow's KB body HTML ships its own <style> and <link rel="stylesheet">
  // tags. Even though the article container is scoped to `.kb-article`, those
  // tags are *global* once injected into the document — they were leaking out
  // and breaking the main app's table styling. Strip them before rendering.
  const safeHtml = useMemo(
    () => stripGlobalStyleTags(data?.body_html ?? ''),
    [data?.body_html],
  );

  const widthClass = maximized
    ? 'w-screen h-screen'
    : 'w-full sm:max-w-2xl lg:max-w-3xl xl:max-w-4xl h-screen';

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-[1px]"
      onClick={onClose}
    >
      <aside
        className={`${widthClass} bg-white shadow-2xl ring-1 ring-slate-200 flex flex-col`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="KB article"
      >
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">
              Reference
            </div>
            <h2 className="text-sm font-semibold text-slate-900 truncate">
              {data?.title || kbNumber}
            </h2>
          </div>
          <button
            type="button"
            className="btn-secondary h-8 px-3 text-xs"
            onClick={() => setMaximized((v) => !v)}
            title={maximized ? 'Restore default width' : 'Expand to full viewport'}
          >
            <MaximizeIcon expanded={maximized} />
            <span>{maximized ? 'Restore' : 'Maximize'}</span>
          </button>
          <button
            type="button"
            className="btn-ghost h-8 w-8"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <p className="text-sm text-slate-500">Loading KB article…</p>}
          {error && (
            <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              Couldn&apos;t load the KB article: {error}
            </p>
          )}
          {data && (
            <div
              className="kb-article"
              /* KB body is ServiceNow-authored HTML; <style>/<link> tags
                 are stripped above, the rest we trust. */
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          )}
          {data?.fetched_at && (
            <p className="mt-6 text-[11px] text-slate-400">
              Cached copy · fetched{' '}
              {new Date(data.fetched_at).toLocaleString()}
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}

/**
 * Remove global `<style>` and `<link rel="stylesheet">` tags from the
 * ServiceNow-authored body HTML before injecting it into the page —
 * otherwise their rules apply globally and degrade the main app's
 * Tailwind-styled tables.
 *
 * Implementation note: regex is fine here because the KB body is not
 * adversarial input (synced from ServiceNow), and we only need to delete
 * specific elements rather than safely sanitise arbitrary markup.
 */
function stripGlobalStyleTags(html: string): string {
  return html
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<link\b[^>]*rel\s*=\s*['"]?stylesheet['"]?[^>]*>/gi, '');
}

function MaximizeIcon({ expanded }: { expanded: boolean }) {
  // Two glyphs: outward arrows for "maximize", inward for "restore".
  if (expanded) {
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M9 7h4M13 7V3M9 7l5-5" />
        <path d="M7 9H3M3 9v4M7 9l-5 5" />
      </svg>
    );
  }
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 3h4v4M13 3l-5 5" />
      <path d="M7 13H3V9M3 13l5-5" />
    </svg>
  );
}
